// 🔧 pages/admin/sponsor.js
// ✅ Purpose: Admin/Manager page to add or update sponsor info
// ✅ Features:
// - Works in Add mode (no ?id= param) or Edit mode (with ?id=...)
// - Loads existing sponsor if editing
// - Preserves old logo unless replaced
// - Uploads logos to /eventAssets/{eventId}/sponsors/
// - Shows 20s success/error alerts

import React, { useState, useEffect, useRef  } from "react";
import { useEventContext } from "../../hooks/use_event_context";
import { db, storage, functions } from "../../firebase";
import {
  doc,
  addDoc,
  updateDoc,
  getDoc,
  collection,
  serverTimestamp,
  getDocs
} from "firebase/firestore";
import {
  ref,
  deleteObject
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { useParams, useNavigate, Link } from "react-router-dom";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import { useUploadWithProgress } from "../../hooks/use_upload_with_progress";
import { showStatus } from "../../utils/status_helpers";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import QRCode from "react-qr-code";
import { httpsCallable } from "firebase/functions";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import { resolvePowerupSnapshot } from "../../utils/powerup_urls";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";

const SponsorForm = () => {

  const { eventId, eventDoc } = useEventContext();
  const { eventslug, id: sponsorId } = useParams();
  const [resolvedSponsorId, setResolvedSponsorId] = useState("");
  const sponsorRecordId = resolvedSponsorId || sponsorId;
  const navigate = useNavigate();
  const { uploadFileWithProgress, progress } = useUploadWithProgress();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    sponsorSlug: "",
    description: "",
    url: "",
    logoFile: null,
    logoUrl: "",
    level: "",
    sponsorAwardTitle: "",
    sponsorContactEmail: "",
    sponsorAwardWinnerReg: "",
    mapMarkerLabel: "",
    mapMarkerTitle: "",
    mapMarkerNote: "",  
    mapMarkerType: "sponsor",
    mapMarkerColor: "success",
    mapMarkerX: "",
    mapMarkerY: "",    
    paymentRequired: false,
    paymentStatus: "not_required",
    approvalStatus: "approved",
    isApproved: true,
    amountCents: 0,
    orderId: "",
    stripeCheckoutSessionId: "",
    sponsorPaymentLinkSent: false,
    sponsorPaymentEmailSent: false, 
    paidAt: null,   
    paymentMethod: "",
    manualApprovalNote: "",
    approvedBy: "",
    approvedAt: null,    
  });
  const [sponsorAwardWinnerEntry, setSponsorAwardWinnerEntry] = useState(null);
  const [sponsorLevels, setSponsorLevels] = useState([]);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [eventMap, setEventMap] = useState("");
  const [imageInfo, setImageInfo] = useState({
    width: null,
    height: null,
    sizeKB: null,
  });

  const resetSponsorForm = () => {
    setResolvedSponsorId("");
    setForm({
      name: "",
      description: "",
      url: "",
      logoFile: null,
      logoUrl: "",
      level: "",
      sponsorAwardTitle: "",
      sponsorContactEmail: "",
      sponsorAwardWinnerReg: "",
      mapMarkerLabel: "",
      mapMarkerTitle: "",
      mapMarkerNote: "",
      mapMarkerType: "sponsor",
      mapMarkerColor: "success",
      mapMarkerX: "",
      mapMarkerY: "",  
      paymentRequired: false,
      paymentStatus: "not_required",
      approvalStatus: "approved",
      isApproved: true,
      amountCents: 0,
      orderId: "",
      stripeCheckoutSessionId: "",
      sponsorPaymentLinkSent: false,
      sponsorPaymentEmailSent: false,    
      paidAt: null,  
      paymentMethod: "",
      manualApprovalNote: "",
      approvedBy: "",
      approvedAt: null,          
    });

    setImageInfo({
      width: null,
      height: null,
      sizeKB: null,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const loadSponsor = async () => {
      if (!eventId || !sponsorId) return;
    try {

      const snap = await resolvePowerupSnapshot(db, eventId, "sponsor", sponsorId);
      if (snap?.exists()) {
        const data = snap.data();
        setResolvedSponsorId(snap.id);
        setForm((prev) => ({
          ...prev,
          name: data.name || "",
          sponsorSlug: data.sponsorSlug || "",
          description: data.description || "",
          url: data.url || "",
          level: data.level || "",
          logoUrl: data.logoUrl || "",
          sponsorAwardTitle: data.sponsorAwardTitle || "",
          sponsorContactEmail: data.sponsorContactEmail || "",
          sponsorAwardWinnerReg: data.sponsorAwardWinnerReg || "",
          mapMarkerLabel: data.mapMarkerLabel || "",
          mapMarkerTitle: data.mapMarkerTitle || "",
          mapMarkerNote: data.mapMarkerNote || "",       
          mapMarkerType: data.mapMarkerType || "sponsor",
          mapMarkerColor: data.mapMarkerColor || "success",
          mapMarkerX: data.mapMarkerX ?? "",
          mapMarkerY: data.mapMarkerY ?? "",    
          paymentRequired: data.paymentRequired === true,
          paymentStatus: data.paymentStatus || "not_required",
          approvalStatus: data.approvalStatus || "approved",
          isApproved: data.isApproved !== false,
          amountCents: Number(data.amountCents || 0),
          orderId: data.orderId || "",
          stripeCheckoutSessionId: data.stripeCheckoutSessionId || "",
          sponsorPaymentLinkSent: data.sponsorPaymentLinkSent === true,
          sponsorPaymentEmailSent: data.sponsorPaymentEmailSent === true,   
          paidAt: data.paidAt || null,
          stripePaymentIntentId: data.stripePaymentIntentId || "",     
          paymentMethod: data.paymentMethod || "",
          manualApprovalNote: data.manualApprovalNote || "",
          approvedBy: data.approvedBy || "",
          approvedAt: data.approvedAt || null,                               
        }));
      }

    } catch (err) {

      logger.error("❌ Error loading sponsor:", {
        value: err
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load sponsor.",
      });

    }
  };

  useEffect(() => {
    if (!eventId) return;

    if (sponsorId) {
      loadSponsor();
    } else {
      resetSponsorForm();
    }
  }, [eventId, sponsorId]);

  useEffect(() => {
    if (!eventId) return;

    const loadEventMap = async () => {
      try {
        const ref = doc(db, "events", eventId, "settings", "voting");
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};

        setEventMap(data.eventMap || "");
      } catch (err) {
        logger.warn("⚠️ Could not load event map", { value: err });
        setEventMap("");
      }
    };

    loadEventMap();
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;

    const loadSponsorLevels = async () => {
      try {
        const ref = doc(db, "events", eventId, "settings", "voting");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          if (Array.isArray(data.sponsorLevels)) {
          setSponsorLevels(
            data.sponsorLevels.map((lvl) => ({
              name: lvl.name || "",

              // ✅ Sponsorship payment support
              // ✅ These fields come from Sponsor Settings
              paymentRequired: lvl.paymentRequired === true,

              amountCents: Number(lvl.amountCents || 0),
            }))
          );
          }
        }
      } catch (err) {
        logger.warn("⚠️ Could not load sponsorLevels", { value: err });
      }
    };

    loadSponsorLevels();
    }, [eventId]);

    useEffect(() => {
    if (!eventId || !form.sponsorAwardWinnerReg) {
      setSponsorAwardWinnerEntry(null);
      return;
    }

    loadSponsorAwardWinnerEntry(form.sponsorAwardWinnerReg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, form.sponsorAwardWinnerReg]);

  useEffect(() => {
    if (!form.logoUrl) {
      setImageInfo({ width: null, height: null, sizeKB: null });
      return;
    }

    const img = new Image();
    img.src = form.logoUrl;

    img.onload = async () => {
      let sizeKB = null;

      try {
        const res = await fetch(form.logoUrl);
        const blob = await res.blob();
        sizeKB = (blob.size / 1024).toFixed(1);
      } catch (err) {
        console.warn("⚠️ Failed to fetch logo size:", err);
      }

      setImageInfo({
        width: img.naturalWidth,
        height: img.naturalHeight,
        sizeKB,
      });
    };
  }, [form.logoUrl]);

  // =====================================================
  // 🎗 Sponsors Add-on Page Gate
  // =====================================================
  // ✅ Purpose:
  // - Prevent direct URL access to sponsor pages when the Sponsors add-on is inactive
  // - Keeps public, sponsor-contact, and manager sponsor tools hidden
  // - Friendly message instead of exposing sponsor functionality
  // =====================================================
  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");

  if (!sponsorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-info text-center mb-0">
            Sponsors are not active for this event.
          </div>
        </div>
      </section>
    );
  }

  // =====================================================
  // 🔤 Sponsor Slug Helper
  // =====================================================
  const buildSponsorSlug = (name = "") =>
    String(name)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const loadSponsorAwardWinnerEntry = async (regNumber) => {
    if (!eventId || !regNumber) {
      setSponsorAwardWinnerEntry(null);
      return;
    }

    try {
      const cleanRegNumber = String(regNumber || "").trim();

      const carsSnap = await getDocs(
        collection(db, "events", eventId, "cars")
      );

      const entryDoc = carsSnap.docs.find((docSnap) => {
        const car = docSnap.data();

        return String(car.regcnt || "").trim() === cleanRegNumber;
      });

      if (!entryDoc) {
        setSponsorAwardWinnerEntry(null);

        showStatus(setStatus, {
          type: "warning",
          message: `No entry found with registration #${cleanRegNumber}.`,
        });

        return;
      }

      setSponsorAwardWinnerEntry({
        id: entryDoc.id,
        ...entryDoc.data(),
      });
    } catch (err) {
      logger.error("❌ Failed to load sponsor award winner entry:", {
        value: err,
      });

      setSponsorAwardWinnerEntry(null);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "logoFile") {
      setForm((prev) => ({ ...prev, logoFile: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleRemoveLogo = async () => {
  if (!form.logoUrl) return;

  try {
    const path = new URL(form.logoUrl).pathname;
    const cleanPath = decodeURIComponent(path.replace(/^\/v0\/b\/[^/]+\/o\//, ""));
    const oldRef = ref(storage, cleanPath);
    await deleteObject(oldRef);
    logger.log("🧹 Logo manually removed", { value: cleanPath });

    setForm((prev) => ({ ...prev, logoUrl: "", logoFile: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";

    showStatus(setStatus, {
      type: "warning",
      message: "Logo removed. You can upload a new one.",
    });

  } catch (err) {
    logger.warn("⚠️ Error removing logo:", { value: err });

    showStatus(setStatus, {
      type: "error",
      message: "Error removing logo.",
    });

  }
};

const getSelectedSponsorLevel = () => {
  return (
    sponsorLevels.find((level) => level.name === form.level) || {
      name: form.level || "",
      paymentRequired: false,
      amountCents: 0,
    }
  );
};

  // Retry delivery against the saved recipient; never submit form email/content.
  const handleSendSponsorNotice = async () => {
    if (saving || !eventId || !sponsorRecordId) return;
    setSaving(true);
    try {
      const { data } = await httpsCallable(functions, "sendSponsorContactNotice")({ eventId, sponsorId: sponsorRecordId });
      showStatus(setStatus, { type: "success", message: data.simulated
        ? "Demo sponsorships do not send external email."
        : data.sending ? "Sponsor notification is already being sent."
        : data.alreadySent ? "A valid sponsor verification email has already been sent."
        : "Sponsor verification email sent successfully." });
    } catch (error) {
      showStatus(setStatus, { type: "error", message: error?.message || "Unable to send sponsor verification email. Please retry." });
    } finally { setSaving(false); }
  };

  const handleSendSponsorPaymentLink = async () => {
    if (!eventId || !sponsorRecordId) {
      showStatus(setStatus, {
        type: "warning",
        message: "Save the sponsor before sending a payment link.",
      });

      return;
    }

    if (!form.sponsorContactEmail) {
      showStatus(setStatus, {
        type: "warning",
        message: "Sponsor contact email is required before sending a payment link.",
      });

      return;
    }

    try {
      setSaving(true);

      console.log("🎗 Sending sponsor payment link:", {
        eventId,
        sponsorId: sponsorRecordId,
        sponsorName: form.name,
        sponsorContactEmail: form.sponsorContactEmail,
        amountCents: form.amountCents,
      });

      const createSponsorCheckoutSession = httpsCallable(
        functions,
        "createSponsorCheckoutSession"
      );

      const result = await createSponsorCheckoutSession({
        eventId,
        sponsorId: sponsorRecordId,
      });

      const data = result?.data || {};

      setForm((prev) => ({
        ...prev,
        paymentRequired: true,
        paymentStatus: "pending",
        approvalStatus: "pending",
        isApproved: false,
        orderId: data.orderId || prev.orderId || "",
        stripeCheckoutSessionId:
          data.sessionId || prev.stripeCheckoutSessionId || "",
        sponsorPaymentLinkSent: true,
        sponsorPaymentEmailSent: true,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Sponsor payment link emailed successfully.",
      });
    } catch (err) {
      logger.error("❌ Failed to send sponsor payment link:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          "Failed to send sponsor payment link. Check Stripe payment settings and sponsor email.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleManualSponsorApproval = async (paymentMethod) => {
    if (!eventId || !sponsorRecordId) return;

    const methodLabels = {
      cash: "cash",
      check: "check",
      waived: "waived fee",
      manual: "manual approval",
    };

    const confirmMessage =
      `Mark this sponsor approved using ${methodLabels[paymentMethod] || "manual approval"}?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setSaving(true);

      const sponsorRef = doc(db, "events", eventId, "sponsors", sponsorRecordId);

      await updateDoc(sponsorRef, {
        paymentStatus:
          paymentMethod === "waived"
            ? "waived"
            : "paid",

        approvalStatus: "approved",

        isApproved: true,

        paymentMethod,

        manualApprovalNote:
          `Sponsor manually approved by manager using ${methodLabels[paymentMethod] || paymentMethod}.`,

        approvedAt: serverTimestamp(),

        updatedAt: serverTimestamp(),
      });

      setForm((prev) => ({
        ...prev,
        paymentStatus:
          paymentMethod === "waived"
            ? "waived"
            : "paid",
        approvalStatus: "approved",
        isApproved: true,
        paymentMethod,
        manualApprovalNote:
          `Sponsor manually approved by manager using ${methodLabels[paymentMethod] || paymentMethod}.`,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Sponsor manually approved.",
      });
    } catch (err) {
      logger.error("❌ Failed to manually approve sponsor:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to manually approve sponsor.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("💾 Sponsor form submit started:", {
      eventId,
      sponsorId,
      name: form.name,
      level: form.level,
      hasLogoFile: !!form.logoFile,
    });

    if (saving) return;

    setSaving(true);
  if (!eventId) {
    showStatus(setStatus, {
      type: "error",
      message: "Event is still loading. Please try again.",
    });

    setSaving(false);
    return;
  }

    if (!String(form.name || "").trim()) {
      showStatus(setStatus, {
        type: "error",
        message: "Sponsor name is required.",
      });

      setSaving(false);
      return;
    }

    try {
      let logoUrl = form.logoUrl || null;
      const selectedSponsorLevel = getSelectedSponsorLevel();

      const sponsorPaymentRequired =
        selectedSponsorLevel.paymentRequired === true &&
        Number(selectedSponsorLevel.amountCents || 0) > 0;

      const sponsorPaymentStatus = sponsorPaymentRequired
        ? "pending"
        : "not_required";

      const sponsorApprovalStatus = sponsorPaymentRequired
        ? "pending"
        : "approved";

      const sponsorIsApproved = sponsorPaymentRequired
        ? false
        : true;
        
      // If new file uploaded, upload and replace existing 
      if (form.logoFile) {
        // ✅ Step 1: Delete old image if present
        if (form.logoUrl) {
          try {
            const path = new URL(form.logoUrl).pathname;
            const cleanPath = decodeURIComponent(path.replace(/^\/v0\/b\/[^/]+\/o\//, ""));
            const oldRef = ref(storage, cleanPath);
            await deleteObject(oldRef);
            logger.log("🧹 Deleted old logo:", { value: cleanPath });
          } catch (err) {
            logger.warn("⚠️ Failed to delete old logo:", { value: err });
          }
        }

        // ✅ Step 2: Upload new image
        const fileExt = form.logoFile.name.split(".").pop();
        const filePath = `eventAssets/${eventId}/sponsors/${uuidv4()}.${fileExt}`;
        const storageRef = ref(storage, filePath);
        logoUrl = await uploadFileWithProgress(storageRef, form.logoFile);

        // ✅ Step 3: Update form to immediately show the new image
        setForm((prev) => ({ ...prev, logoUrl }));
      }

      if (sponsorRecordId) {
        // 🔁 UPDATE existing
        const docRef = doc(db, "events", eventId, "sponsors", sponsorRecordId);
        await updateDoc(docRef, {
          name: form.name.trim(),
          description: form.description,
          url: form.url,
          level: form.level || "",
          sponsorLevelName: form.level || "",

          paymentRequired: sponsorPaymentRequired,

          amountCents: Number(selectedSponsorLevel.amountCents || 0),

          paymentStatus:
            form.paymentStatus === "paid"
              ? "paid"
              : sponsorPaymentStatus,

          approvalStatus:
            form.paymentStatus === "paid"
              ? "approved"
              : sponsorApprovalStatus,

          isApproved:
            form.paymentStatus === "paid"
              ? true
              : sponsorIsApproved,

          sponsorAwardTitle: form.sponsorAwardTitle || "",
          sponsorContactEmail: String(form.sponsorContactEmail || "").trim().toLowerCase(),
          logoUrl,
          updatedAt: serverTimestamp(),
          sponsorAwardWinnerReg: String(form.sponsorAwardWinnerReg || "").trim(),
          mapMarkerLabel: String(form.mapMarkerLabel || "").trim(),
          mapMarkerTitle: String(form.mapMarkerTitle || "").trim(),
          mapMarkerNote: String(form.mapMarkerNote || "").trim(),
          mapMarkerType: "sponsor",
          mapMarkerColor: "success",
          mapMarkerX:
            form.mapMarkerX === "" || form.mapMarkerX === null
              ? null
              : Number(form.mapMarkerX),
          mapMarkerY:
            form.mapMarkerY === "" || form.mapMarkerY === null
              ? null
              : Number(form.mapMarkerY),          
        });
        const slugResult = await httpsCallable(functions, "updatePowerupSlug")({ eventId, family: "sponsor", entityId: sponsorRecordId, slug: form.sponsorSlug || undefined });
        setForm((previous) => ({ ...previous, sponsorSlug: slugResult.data.slug }));
        navigate(`/${eventslug}/sponsor/${slugResult.data.slug}`, { replace: true });

        showStatus(setStatus, {
          type: "success",
          message: `Updated sponsor '${form.name}'.`,
        });

      } else {

        // ➕ ADD new
        const newSponsorRef = await addDoc(collection(db, "events", eventId, "sponsors"), {
          name: form.name.trim(),
          description: form.description || "",
          url: form.url || "",
          logoUrl,
          level: form.level || "",
          sponsorLevelName: form.level || "",

          paymentRequired: sponsorPaymentRequired,

          amountCents: Number(selectedSponsorLevel.amountCents || 0),

          paymentStatus: sponsorPaymentStatus,

          approvalStatus: sponsorApprovalStatus,

          isApproved: sponsorIsApproved,

          orderId: "",

          stripeCheckoutSessionId: "",

          stripePaymentIntentId: "",

          paidAt: null,          
          sponsorAwardTitle: form.sponsorAwardTitle || "",
          sponsorContactEmail: String(form.sponsorContactEmail || "").trim().toLowerCase(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          sponsorAwardWinnerReg: String(form.sponsorAwardWinnerReg || "").trim(),
          mapMarkerLabel: String(form.mapMarkerLabel || "").trim(),
          mapMarkerTitle: String(form.mapMarkerTitle || "").trim(),
          mapMarkerNote: String(form.mapMarkerNote || "").trim(),         
          mapMarkerType: "sponsor",
          mapMarkerColor: "success",
          mapMarkerX:
            form.mapMarkerX === "" || form.mapMarkerX === null
              ? null
              : Number(form.mapMarkerX),
          mapMarkerY:
            form.mapMarkerY === "" || form.mapMarkerY === null
              ? null
              : Number(form.mapMarkerY),           
        });
        // Keep the saved identity if slug allocation needs a retry.
        setResolvedSponsorId(newSponsorRef.id);
        const slugResult = await httpsCallable(functions, "updatePowerupSlug")({ eventId, family: "sponsor", entityId: newSponsorRef.id, slug: form.sponsorSlug || undefined });
        setForm((previous) => ({ ...previous, sponsorSlug: slugResult.data.slug }));

        showStatus(setStatus, {
          type: "success",
          message: `Sponsor '${form.name}' added successfully. You can continue editing it here.`,
        });

        // ✅ Keep the saved information visible on screen
        setForm((prev) => ({
          ...prev,
          logoFile: null,
          logoUrl,
        }));

        // ✅ Clear only the file input, not the sponsor form data
        if (fileInputRef.current) fileInputRef.current.value = "";

        // ✅ Move from Add mode to Edit mode so QR/edit behavior works
        navigate(`/${eventslug}/sponsor/${slugResult.data.slug}`, { replace: true });

      }
    } catch (err) {
      logger.error("🔥 Error saving sponsor:", {
        value: err
      });

      showStatus(setStatus, {
        type: "error",
        message: "Error saving sponsor.",
      });

    } finally {
      setSaving(false);
    }

  };

  const handlePrintQr = () => {
    const printContent = document.getElementById("print-qr");
    const printWindow = window.open("", "", "width=800,height=800");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
            }
            #qr-container {
              width: 6in;
              height: 6in;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img, svg {
              max-width: 100%;
              max-height: 100%;
            }
          </style>
        </head>
        <body>
          <div id="qr-container">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
    };

    const handleMapClick = (e) => {
      if (!eventMap) return;

      const rect = e.currentTarget.getBoundingClientRect();

      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      setForm((prev) => ({
        ...prev,
        mapMarkerType: "sponsor",
        mapMarkerColor: "success",
        mapMarkerX: Number(x.toFixed(2)),
        mapMarkerY: Number(y.toFixed(2)),
      }));
    };

    const clearMapMarkerPlacement = () => {
    setForm((prev) => ({
      ...prev,
      mapMarkerX: "",
      mapMarkerY: "",
    }));

    showStatus(setStatus, {
      type: "warning",
      message: "Sponsor map marker placement cleared.",
    });
  };

  // =====================================================
  // 💳 Sponsor Payment Add-on Gate
  // =====================================================
  // ✅ Purpose:
  // - Sponsor management is included in the core LGV Event Package
  // - Only online Sponsor Payments require the Sponsor Payments add-on
  // - Uses purchasedAddOns.sponsorPayments as the source of truth
  // =====================================================
  const sponsorPaymentsEnabled = hasPurchasedAddOn(
    eventDoc,
    "sponsorPayments"
  );

  const formatCents = (amountCents = 0) => {
    return `$${(Number(amountCents || 0) / 100).toFixed(2)}`;
  };

  const formatTimestamp = (value) => {
    if (!value) return "Not recorded";

    const date = value?.toDate ? value.toDate() : new Date(value);

    if (Number.isNaN(date.getTime())) return "Not recorded";

    return date.toLocaleString();
  };

  return (
    <section id="top-content" className="container-fluid py-5">

      <div className="row mb-4">
          <div className="col text-start">
          <h1 className="display-5">
              {sponsorId ? "✏️ Edit Sponsor" : "🎗️ Add Sponsor"}
          </h1>
          <p className="lead text-muted">
              {sponsorId ? "Update sponsor details" : "Add a sponsor to this event"}
          </p>
          </div>
          <div className="col-auto align-self-start">
            <PromotionSourceButton source="sponsors" sourceId={resolvedSponsorId} />
          </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>

          <div className="row mb-4">

            {/* Column 1: QR Code + Actions */}
            <div className="col-md-4 text-center mb-3 mb-md-0">

              {sponsorId && eventslug && (

                <>
                  <div
                    id="print-qr"
                    onClick={handlePrintQr}
                    className="d-inline-block cursor-pointer"
                    style={{ cursor: "pointer" }}
                    title="Click to print QR"
                  >

                  <QRCode
                          value={`/${eventslug}/sponsors/${form.sponsorSlug || sponsorRecordId}`}
                    size={250}
                  />
                    <div className="small text-muted mt-2">Click QR to print</div>
                  </div>
                  <div className="small text-muted mt-2">Scan to view public sponsor page</div>
                </>
              )}
            </div>

            {/* Column 2: Name and Description fields */}
            <div className="col-md-8">

              {/* ➕ Add New Sponsor */}
              {eventslug && (
                <div className="mb-3">

                  <Link
                    to={`/${eventslug}/sponsor`}
                    className="btn btn-primary"
                  >
                    ➕ Add New Sponsor
                  </Link>

                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-bold">Sponsor Name *</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Public URL slug</label>
                <input type="text" className="form-control" name="sponsorSlug" value={form.sponsorSlug} onChange={handleChange} placeholder={buildSponsorSlug(form.name) || "sponsor-name"} />
                <div className="form-text">Lowercase letters, numbers, and single hyphens. Leave empty to keep or create the automatic URL.</div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Description</label>
                <ReactQuill
                  theme="snow"
                  value={form.description}
                  onChange={(val) => setForm((prev) => ({ ...prev, description: val }))}
                />
              </div>
            </div>
          </div>


        <div className="mb-3">
            <label className="form-label fw-bold">Website URL</label>
            <input
            type="url"
            className="form-control"
            name="url"
            value={form.url}
            onChange={handleChange}
            placeholder="https://sponsor.com"
            />
        </div>

        { sponsorLevels.length > 0 && (
          <div className="mb-3">
            <label className="form-label fw-bold">Sponsor Level</label>
              <select
                className="form-select"
                name="level"
                value={form.level}
                onChange={handleChange}
              >
              <option value="">Select level...</option>
                {sponsorLevels.map((level, idx) => (
                  <option key={idx} value={level.name}>
                    {level.name}
                    {level.paymentRequired && level.amountCents > 0
                      ? ` — $${(level.amountCents / 100).toFixed(2)}`
                      : ""}
                  </option>
                ))}
            </select>
          </div>
        )}

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <strong>🗺️ Sponsor Map Location</strong>
          </div>

          <div className="card-body">
            <p className="text-muted mb-3">
              Optional. Enter a printed map marker label, or click directly on the event map to place this sponsor.
            </p>

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-4">
                <label className="form-label fw-bold">Map Marker Label</label>

                <input
                  type="text"
                  className="form-control"
                  name="mapMarkerLabel"
                  value={form.mapMarkerLabel}
                  onChange={handleChange}
                  placeholder="Example: 7"
                />
              </div>

              <div className="col-12 col-md-8">
                <label className="form-label fw-bold">Map Marker Title</label>

                <input
                  type="text"
                  className="form-control"
                  name="mapMarkerTitle"
                  value={form.mapMarkerTitle}
                  onChange={handleChange}
                  placeholder="Example: Booth 7 near registration"
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-bold">Map Marker Note</label>

                <textarea
                  className="form-control"
                  name="mapMarkerNote"
                  value={form.mapMarkerNote}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Example: Located near the main entrance beside the registration table."
                />
              </div>
            </div>

            {eventMap ? (
              <>
                <div className="alert alert-info">
                  Click the map below to place the sponsor marker. Sponsors use a green marker.
                </div>

                <div
                  className="position-relative border rounded overflow-hidden bg-light"
                  onClick={handleMapClick}
                  style={{
                    cursor: "crosshair",
                    maxWidth: "100%",
                  }}
                >
                  <img
                    src={eventMap}
                    alt="Event Map"
                    className="img-fluid d-block w-100"
                    style={{
                      objectFit: "contain",
                    }}
                  />

                  {form.mapMarkerX !== "" && form.mapMarkerY !== "" && (
                    <div
                      className="position-absolute translate-middle bg-success text-white rounded-circle d-flex align-items-center justify-content-center fw-bold shadow"
                      style={{
                        left: `${form.mapMarkerX}%`,
                        top: `${form.mapMarkerY}%`,
                        width: "36px",
                        height: "36px",
                        border: "3px solid #fff",
                        pointerEvents: "none",
                      }}
                      title={form.mapMarkerTitle || form.name || "Sponsor Marker"}
                    >
                      {form.mapMarkerLabel || "S"}
                    </div>
                  )}
                </div>

                <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mt-3">
                  <div className="small text-muted">
                    Current placement:{" "}
                    {form.mapMarkerX !== "" && form.mapMarkerY !== ""
                      ? `${form.mapMarkerX}% X, ${form.mapMarkerY}% Y`
                      : "No marker placed yet."}
                  </div>

                  {(form.mapMarkerX !== "" || form.mapMarkerY !== "") && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={clearMapMarkerPlacement}
                    >
                      Clear Marker Placement
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="alert alert-warning mb-0">
                No event map has been uploaded yet. Upload the event map from Event Setup → Images first.
              </div>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">
            🏆 Sponsor Award Title
          </label>

          <input
            type="text"
            className="form-control"
            value={form.sponsorAwardTitle}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                sponsorAwardTitle: e.target.value,
              }))
            }
            placeholder="Best Paint, People's Choice, Best Interior, etc."
          />

          <div className="form-text">
            Optional sponsor award associated with this sponsor.
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">
            🏁 Sponsor Award Winner Registration #
          </label>

          <input
            type="text"
            className="form-control"
            name="sponsorAwardWinnerReg"
            value={form.sponsorAwardWinnerReg}
            onChange={handleChange}
            placeholder="Enter entry registration number"
          />

          <div className="form-text">
            Enter the registration number shown on the winning entry.
          </div>

          {sponsorAwardWinnerEntry ? (
            <div className="alert alert-success mt-3 mb-0">
              <strong>✅ Award Winner Linked</strong>
              <br />

              <div className="mt-2">
                <strong>
                  #{sponsorAwardWinnerEntry.regcnt}
                </strong>
                {" — "}
                {sponsorAwardWinnerEntry.description ||
                  `${sponsorAwardWinnerEntry.year || ""} ${sponsorAwardWinnerEntry.model || ""}`.trim() ||
                  "Entry details unavailable"}
              </div>

              {sponsorAwardWinnerEntry.imgUrl && (
                <div className="mt-3">
                  <img
                    src={sponsorAwardWinnerEntry.imgUrl}
                    alt={`Entry #${sponsorAwardWinnerEntry.regcnt}`}
                    className="img-fluid rounded border"
                    style={{
                      maxHeight: "250px",
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
              
              {(
                sponsorAwardWinnerEntry.ownerName ||
                sponsorAwardWinnerEntry.ownerFirst ||
                sponsorAwardWinnerEntry.ownerLast ||
                sponsorAwardWinnerEntry.name
              ) && (
                <div className="small text-muted">
                  👤 Owner: {
                    sponsorAwardWinnerEntry.ownerName ||
                    `${sponsorAwardWinnerEntry.ownerFirst || ""} ${sponsorAwardWinnerEntry.ownerLast || ""}`.trim() ||
                    sponsorAwardWinnerEntry.name
                  }
                </div>
              )}

            </div>
          ) : form.sponsorAwardWinnerReg ? (
            <div className="alert alert-warning mt-3 mb-0">
              ⚠ No entry found for registration #{form.sponsorAwardWinnerReg}.
              Save the sponsor after correcting the registration number.
            </div>
          ) : null}
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">
            📧 Sponsor Point of Contact Email
          </label>

          <input
            type="email"
            className="form-control"
            name="sponsorContactEmail"
            value={form.sponsorContactEmail}
            onChange={handleChange}
            placeholder="contact@sponsor.com"
          />

          <div className="form-text">
            Optional. This user will be invited or notified that they can review and update this sponsor’s public information.
          </div>
          {sponsorRecordId && <button type="button" className="btn btn-outline-primary mt-2" disabled={saving}
            onClick={handleSendSponsorNotice}>Send Sponsor Verification Email</button>}
          <div className="form-text">New sponsors with a contact email receive a verification link automatically. Save contact changes before sending.</div>
        </div>

        {form.logoUrl && (
          <div className="mb-3">
            <label className="form-label fw-bold">Current Logo</label>
            <div className="d-flex align-items-center gap-3">
              <img
                src={form.logoUrl}
                alt="Logo"
                style={{ maxHeight: "50px", maxWidth: "150px", border: "1px solid #ccc" }}
              />
              <div>
                {imageInfo.width && imageInfo.height && (
                  <div className="text-muted small mb-1">
                    {imageInfo.width}×{imageInfo.height}px
                    {imageInfo.sizeKB && ` · ${imageInfo.sizeKB} KB`}
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleRemoveLogo}
                  title="Remove logo"
                >
                  ❌ Remove
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3">
            <label className="form-label fw-bold">Logo Image</label>
            <input
              type="file"
              className="form-control"
              name="logoFile"
              accept="image/*"
              onChange={handleChange}
              ref={fileInputRef}
            />
        </div>

        {progress > 0 && progress < 100 && (
          <div className="mb-3">
            <label className="form-label">Upload Progress</label>
            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${progress}%` }}
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {progress}%
              </div>
            </div>
          </div>
        )}

        {sponsorId && (
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>💳 Sponsorship Payment</strong>
            </div>

            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-4">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">Payment Required</div>
                    <div className="fw-bold">
                      {form.paymentRequired ? "Yes" : "No"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">Amount</div>
                    <div className="fw-bold">
                      ${(Number(form.amountCents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">Status</div>

                    {form.paymentStatus === "paid" ? (
                      <span className="badge bg-success">
                        Paid / Approved
                      </span>
                    ) : form.paymentStatus === "pending" ? (
                      <span className="badge bg-warning text-dark">
                        Payment Pending
                      </span>
                    ) : (
                      <span className="badge bg-secondary">
                        No Payment Required
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {form.paymentRequired && form.paymentStatus !== "paid" && (
                <div className="alert alert-warning">
                  This sponsor requires payment before it becomes publicly approved.
                </div>
              )}

              {form.paymentStatus === "paid" && (
                <div className="alert alert-success">
                  This sponsor has paid and is approved.
                </div>
              )}

              {form.orderId && (
                <div className="small text-muted mb-3">
                  Order ID: {form.orderId}
                </div>
              )}

              {!sponsorPaymentsEnabled && (
                <div className="alert alert-warning">
                  Sponsorship payments are not enabled for this event yet. Go to Event Package & Add-ons and purchase or enable Sponsor Payments.
                </div>
              )}

              <div className="border rounded p-3 bg-light mb-3">
                <h6 className="fw-bold mb-3">Payment Audit</h6>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="small text-muted">Amount</div>
                    <div className="fw-bold">
                      {formatCents(form.amountCents)}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="small text-muted">Approval Status</div>
                    <div className="fw-bold">
                      {form.approvalStatus || "Not recorded"}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="small text-muted">Order ID</div>
                    <div className="fw-bold text-break">
                      {form.orderId || "Not created yet"}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="small text-muted">Checkout Session</div>
                    <div className="fw-bold text-break">
                      {form.stripeCheckoutSessionId || "Not created yet"}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="small text-muted">Payment Intent</div>
                    <div className="fw-bold text-break">
                      {form.stripePaymentIntentId || "Not recorded yet"}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="small text-muted">Paid At</div>
                    <div className="fw-bold">
                      {formatTimestamp(form.paidAt)}
                    </div>
                  </div>
                </div>
              </div>

              {form.paymentStatus !== "paid" && form.approvalStatus !== "approved" && (
                <div className="border rounded p-3 mb-3">
                  <h6 className="fw-bold mb-2">Manager Override</h6>

                  <p className="text-muted small mb-3">
                    Use this only when a sponsor paid outside Stripe, has a comp agreement,
                    or should be approved manually.
                  </p>

                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                      onClick={() => handleManualSponsorApproval("cash")}
                      disabled={saving}
                    >
                      Mark Paid Cash
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                      onClick={() => handleManualSponsorApproval("check")}
                      disabled={saving}
                    >
                      Mark Paid Check
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleManualSponsorApproval("waived")}
                      disabled={saving}
                    >
                      Waive Fee
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handleManualSponsorApproval("manual")}
                      disabled={saving}
                    >
                      Approve Manually
                    </button>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="btn btn-success"
                onClick={handleSendSponsorPaymentLink}
                disabled={
                  saving ||
                  !sponsorPaymentsEnabled ||
                  !form.paymentRequired ||
                  form.paymentStatus === "paid"
                }
              >
                {form.sponsorPaymentEmailSent
                  ? "📧 Resend Sponsor Payment Link"
                  : "📧 Send Sponsor Payment Link"}
              </button>

              {!form.paymentRequired && (
                <div className="form-text mt-2">
                  This sponsor level does not require payment.
                </div>
              )}
            </div>
          </div>
        )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : sponsorId
              ? "💾 Update Sponsor"
              : "➕ Save Sponsor"}
          </button>
        </form>

    </div>
    </section>
  );
};

export default SponsorForm;
